import React from 'react';
import { Card } from '../ui/Card';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { CategorySummary } from '../../types/financial';
import { formatCurrency } from '../../utils/pdfGenerator';
import './Financial.css';

interface CategoryChartProps {
  data: CategorySummary[];
  type: 'receita' | 'despesa';
  loading?: boolean;
}

const COLORS = [
  '#10b981',
  '#3b82f6',
  '#8b5cf6',
  '#f59e0b',
  '#ef4444',
  '#ec4899',
  '#14b8a6',
  '#84cc16',
  '#f97316',
  '#64748b',
  '#06b6d4',
  '#a855f7',
];

export const CategoryChart: React.FC<CategoryChartProps> = ({ data, type, loading }) => {
  if (loading) {
    return (
      <Card title={`${type === 'receita' ? 'Receitas' : 'Despesas'} por Categoria`}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Carregando dados...</p>
        </div>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card title={`${type === 'receita' ? 'Receitas' : 'Despesas'} por Categoria`}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Nenhum dado disponível para o período selecionado.</p>
        </div>
      </Card>
    );
  }

  const filteredData = data.filter(item => item.type === type);
  const topCategories = filteredData.slice(0, 10);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: 'var(--page-surface)',
            padding: '12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>
            {data.name || data.categoryName}
          </p>
          <p style={{ margin: '4px 0', color: 'var(--page-text-muted)', fontSize: '14px' }}>
            Total: {formatCurrency(data.value || data.total)}
          </p>
          <p style={{ margin: '4px 0', color: 'var(--page-text-muted)', fontSize: '14px' }}>
            Transações: {data.count || 0}
          </p>
          <p style={{ margin: '4px 0', color: 'var(--page-text-muted)', fontSize: '14px' }}>
            Percentual: {(data.percentage || 0).toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card title={`${type === 'receita' ? 'Receitas' : 'Despesas'} por Categoria`}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginTop: '16px' }}>
        <div style={{ width: '100%', height: '350px' }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={topCategories.map(cat => ({
                  name: cat.categoryName,
                  value: cat.total,
                  percentage: cat.percentage,
                  count: cat.count,
                  categoryId: cat.categoryId,
                  type: cat.type,
                }))}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => `${entry.name}: ${entry.percentage?.toFixed(1) || 0}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {topCategories.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={{ width: '100%', height: '350px' }}>
          <ResponsiveContainer>
            <BarChart data={topCategories.map(cat => ({
              name: cat.categoryName,
              value: cat.total,
              percentage: cat.percentage,
              count: cat.count,
              categoryId: cat.categoryId,
              type: cat.type,
            }))} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--page-border)" />
              <XAxis
                type="number"
                stroke="var(--page-text-muted)"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => {
                  if (value >= 1000) {
                    return `R$ ${(value / 1000).toFixed(1)}k`;
                  }
                  return `R$ ${value}`;
                }}
              />
              <YAxis
                type="category"
                dataKey="name"
                stroke="var(--page-text-muted)"
                style={{ fontSize: '12px' }}
                width={120}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="value"
                fill={type === 'receita' ? '#10b981' : '#ef4444'}
                radius={[0, 8, 8, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {topCategories.length < filteredData.length && (
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: 'var(--page-surface-hover)', borderRadius: '8px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--page-text-muted)' }}>
            Mostrando as 10 principais categorias de {filteredData.length} no total.
          </p>
        </div>
      )}
    </Card>
  );
};

